import React, { useEffect, useState } from "react";
import { View, StyleSheet, Platform, Modal, Pressable } from "react-native";
import { useCall } from "@/hooks/useCall";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Avatar from "@/components/Avatar";
import { formatPhoneNumber } from "@/lib/phone";

const MODAL_WIDTH = 360;
const MODAL_HEIGHT = 480;

export function CallModal() {
  const { callState, currentCall, endCall, answerCall, toggleMinimize, isMuted, toggleMute, isSpeaker, toggleSpeaker } =
    useCall();
  const { colors } = useTheme();
  const [duration, setDuration] = useState(0);

  // Timer for active calls - called unconditionally
  useEffect(() => {
    if (callState === "active" && currentCall?.startTime) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - currentCall.startTime!.getTime()) / 1000);
        setDuration(elapsed);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [callState, currentCall?.startTime]);

  // Early return after all hooks
  if (!currentCall) return null;

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const modalContent = (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.card,
          borderColor: colors.border,
          width: MODAL_WIDTH,
          height: MODAL_HEIGHT,
        },
      ]}
    >
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={styles.headerSpacer} />
        {Platform.OS === "web" && (
          <Button icon="window-minimize" iconSize={18} style={styles.minimizeBtn} onPress={toggleMinimize} />
        )}
      </View>

      {/* Call info */}
      <View style={styles.content}>
        {currentCall.callerUserId ? (
          <Avatar userId={currentCall.callerUserId} size={100} />
        ) : (
          <Avatar
            firstName={currentCall.callerName.split(" ")[0]}
            lastName={currentCall.callerName.split(" ").slice(1).join(" ")}
            size={100}
          />
        )}

        <ThemedText style={styles.callerName}>{currentCall.callerName}</ThemedText>

        {currentCall.phoneNumber && (
          <ThemedText style={[styles.phoneNumber, { color: colors.textSecondary }]}>
            {formatPhoneNumber(currentCall.phoneNumber)}
          </ThemedText>
        )}

        <ThemedText style={[styles.fromNumber, { color: colors.placeholder }]}>
          from {formatPhoneNumber(currentCall.fromNumber)}
        </ThemedText>

        <ThemedText style={[styles.status, { color: colors.textSecondary }]}>
          {callState === "incoming" && "Incoming call..."}
          {callState === "outgoing" && "Calling..."}
          {callState === "active" && formatDuration(duration)}
        </ThemedText>
      </View>

      {/* Controls */}
      <View style={styles.controls}>
        {callState === "incoming" ? (
          <>
            <Button
              icon="phone"
              iconSize={28}
              style={[styles.controlBtn, { backgroundColor: "#22c55e" }]}
              onPress={answerCall}
            />
            <Button
              icon="phone-hangup"
              iconSize={28}
              style={[styles.controlBtn, { backgroundColor: "#ef4444" }]}
              onPress={endCall}
            />
          </>
        ) : (
          <>
            <Button
              icon={isMuted ? "microphone-off" : "microphone"}
              iconSize={24}
              style={[styles.controlBtn, isMuted && { backgroundColor: colors.primary }]}
              onPress={toggleMute}
            />
            <Button
              icon={isSpeaker ? "volume-high" : "volume-medium"}
              iconSize={24}
              style={[styles.controlBtn, isSpeaker && { backgroundColor: colors.primary }]}
              onPress={toggleSpeaker}
            />
            <Button
              icon="phone-hangup"
              iconSize={28}
              style={[styles.controlBtn, { backgroundColor: "#ef4444" }]}
              onPress={endCall}
            />
          </>
        )}
      </View>
    </View>
  );

  // Web uses Modal for blocking behavior, native renders directly
  if (Platform.OS === "web") {
    return (
      <Modal visible transparent animationType="fade">
        <View style={styles.overlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => {}} />
          {modalContent}
        </View>
      </Modal>
    );
  }

  return modalContent;
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    borderRadius: 12,
    borderWidth: 1,
    boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.3)",
    elevation: 8,
  },
  header: {
    height: 40,
    borderBottomWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
  },
  headerSpacer: {
    flex: 1,
  },
  minimizeBtn: {
    width: 32,
    height: 32,
    backgroundColor: "transparent",
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 20,
  },
  callerName: {
    fontSize: 24,
    fontWeight: "600",
    marginBottom: 8,
  },
  phoneNumber: {
    fontSize: 16,
  },
  fromNumber: {
    fontSize: 13,
  },
  status: {
    fontSize: 14,
  },
  controls: {
    flexDirection: "row",
    justifyContent: "space-around",
    padding: 24,
    gap: 16,
  },
  controlBtn: {
    width: 60,
    height: 60,
    borderRadius: 30,
  },
});
